import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';

import updateAiContentItems from 'common/src/modules/aiContent/store/aiContentProject/thunks/updateAiContentItems';
import {AiContentProjectAgentToolName} from '../aiContentProjectAgent.enums';

const inputSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.number().describe('The ID of the item to update'),
        name: z.string().optional().nullable().describe('The new name of the item'),
        inputValues: z
          .record(z.string())
          .optional()
          .nullable()
          .describe('Key-value pairs of input column values to update for this item'),
      }),
    )
    .describe('Array of items to update with their new values'),
});

export default function updateItemsTool(store: CLStore) {
  async function execute({
    input: {items},
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    const state = store.getState();
    const project = state.aiContentProjectPage?.project;

    if (!project?.id) {
      return toolError('No project is currently loaded. Cannot update items.');
    }

    if (!items || items.length === 0) {
      return toolError('No items provided. Please specify at least one item to update.');
    }

    const existingItems = state.aiContentProjectPage?.items ?? [];

    const updates = items.map((item) => {
      const existingItem = existingItems.find((existing) => existing.id === item.id);
      const hasInputValuesToUpdate = item.inputValues !== undefined && item.inputValues !== null;

      return {
        id: item.id,
        request: {
          ...(item.name !== undefined && item.name !== null && {name: item.name}),
          ...(hasInputValuesToUpdate && {
            inputValues: {
              ...existingItem?.inputValues,
              ...item.inputValues,
            },
          }),
        },
      };
    });

    try {
      await store.dispatch(updateAiContentItems(updates)).unwrap();

      const itemIds = items.map((item) => item.id).join(', ');

      return toolResult(
        `Successfully updated ${items.length} item${items.length > 1 ? 's' : ''} (IDs: ${itemIds})`,
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);

      return toolError(`Error updating items: ${errorMessage}`);
    }
  }

  return tool({
    displayName: 'Updating items',
    name: AiContentProjectAgentToolName.UpdateItems,
    description:
      'Updates one or more existing AI content items. Can update the name and/or input values of each item. Use this tool even when updating a single item.',
    execute,
    inputSchema,
  });
}

